import { ReceptionBeanTranslator, ReceptionModelTranslator } from "./translators";
import { DomainRegistry } from "../../../domain";
import { IDocumentRepository, Document, UnmanagedDocumentSummaryInformation, CurrentEditors } from "../../../domain/document";
import { IWorkflowRepository } from "../../../domain/workflow/IWorkflowRepository";
import { ReceptionSaveResult, WorkflowActionContent } from "../../../domain/workflow";
import { ReceptionListDispInfo, ReceptionDetailListDispInfo, ReceptionTodoListDispInfo, ReceptionDetailSummary } from "../../../domain/workflow/list";
import {
  ReceptionSearchCondition,
  ReceptionSearchResult,
  ReceptionDetailSearchResult,
  ReceptionDetailSearchCondition,
  ReceptionDetailProgressSearchCondition,
  ReceptionTodoSearchCondition,
  ReceptionDetailPresetSearchCondition,
  ReceptionDetailPresetSortCondition,
  ReceptionDetailCsvFilePrint,
} from "../../../domain/workflow/search";
import { ReceptionEditor, WorkflowRouteEditorModel } from "../../../domain/workflow/editor";
import { ReceptionComment, ReceptionPatient, ReceptionCommentSet, ReceptionManager } from "../../../domain/workflow/reception";
import { WorkflowDocumentConflictKinds, ReceptionCommentKinds, ReceptionCommentKindsName } from "../../../domain/workflow/kinds";
import { ReceptionReference } from "../../../domain/workflow/reference";
import { WorkflowDocumentSaveResult, WorkflowDocument, WorkflowDocumentViewer, DispatchDocumentResult, WorkflowDocumentInfo, WorkflowHisInformation } from "../../../domain/workflow/document";
import { LoggerFactory } from "../../../utilities/logger";
import { AuthenticatedService } from "../../../infrastructure";
import { AsyncUtility } from "../../../utilities";
import { IWorkflowDocumentSupplementBean, IWorkflowDocumentOperationMessageBean } from "./beans/document";
import { AxiosRequestConfig } from "axios";
import { ReceptionDetailListItem } from "../../../domain/workflow/items";
import { DocumentManageKinds } from "../../../domain/system";
import { IReceptionDetailCsvFilePrintBean } from "./beans/search";

export class WorkflowRepository extends AuthenticatedService implements IWorkflowRepository {
  constructor(getToken: () => string) {
    super(getToken);
    this._beanTranslator = new ReceptionBeanTranslator(getToken);
    this._modelTranslator = new ReceptionModelTranslator(getToken);
  }

  private readonly _beanTranslator: ReceptionBeanTranslator;
  private readonly _modelTranslator: ReceptionModelTranslator;

  private _logger = LoggerFactory.getLogger();

  /**
   * ユーザ種別取得
   */
  async getReceptionUserType(): Promise<number> {
    const url = "/api/reception/usertype";
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return result.data as number;
  }

  /**
   * 依頼一覧用表示設定取得
   */
  async getReceptionListInitSetting(): Promise<ReceptionListDispInfo> {
    const url = "/api/receptions/init";
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return this._beanTranslator.toReceptionListDispInfo(result.data);
  }

  /**
   * 依頼詳細一覧（医事向けWF一覧）用表示設定取得
   */
  async getReceptionDetailListInitSetting(): Promise<ReceptionDetailListDispInfo> {
    const url = "/api/receptiondetails/init";
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return this._beanTranslator.toReceptionDetailListDispInfo(result.data);
  }

  /**
   * Todoリスト用表示設定取得
   */
  async getReceptionTodoListInitSetting(): Promise<ReceptionTodoListDispInfo> {
    const url = "/api/receptiontodo/init";
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return this._beanTranslator.toReceptionTodoListDispInfo(result.data);
  }

  /**
   * 依頼受付検索
   * @param conditions 
   */
  async searchReception(conditions: ReceptionSearchCondition): Promise<ReceptionSearchResult> {
    const url = "/api/receptions/search";
    const query = {
      params: {
        token: this.token,
      }
    };
    const conditionBean = this._modelTranslator.toReceptionSearchConditionBean(conditions);
    this.outDataLog(conditionBean);
    const result = await this.post(url, conditionBean, query);
    this.outDataLog(result.data);
    return this._beanTranslator.toReceptionSearchResult(result.data);
  }

  /**
   * 依頼詳細検索
   * @param conditions 
   */
  async searchReceptionDetail(conditions: ReceptionDetailSearchCondition): Promise<ReceptionDetailSearchResult> {
    const url = "/api/receptiondetails/search";
    const query = {
      params: {
        token: this.token,
      }
    };
    const conditionBean = this._modelTranslator.toReceptionDetailSearchConditionBean(conditions);
    this.outDataLog(conditionBean);
    const result = await this.post(url, conditionBean, query);
    this.outDataLog(result.data);

    return this._beanTranslator.toReceptionDetailSearchResult(result.data);
  }

  /**
   * 依頼詳細(WF状態)検索
   * @param conditions 
   */
  async searchReceptionDetailProgress(conditions: ReceptionDetailProgressSearchCondition): Promise<ReceptionDetailSearchResult> {
    const url = "/api/receptiondetails/search-progress";
    const query = {
      params: {
        token: this.token,
      }
    };
    const conditionBean = this._modelTranslator.toReceptionDetailProgressSearchConditionBean(conditions);
    this.outDataLog(conditionBean);
    const result = await this.post(url, conditionBean, query);
    this.outDataLog(result.data);

    return this._beanTranslator.toReceptionDetailSearchResult(result.data);
  }

  /**
   * 依頼詳細(プリセット)検索
   * @param conditions 
   */
  async searchReceptionDetailPreset(conditions: ReceptionDetailSearchCondition): Promise<ReceptionDetailSearchResult> {
    const url = "/api/receptiondetails/search-preset";
    const query = {
      params: {
        token: this.token,
      }
    };
    const conditionBean = this._modelTranslator.toReceptionDetailAllSearchConditionBean(conditions);
    this.outDataLog(conditionBean);
    const result = await this.post(url, conditionBean, query);
    this.outDataLog(result.data);

    return this._beanTranslator.toReceptionDetailSearchResult(result.data);
  }

  async searchManagerHistoryReceptionDetail(conditions: ReceptionDetailSearchCondition): Promise<ReceptionDetailSearchResult> {
    const url = "/api/receptiondetails/manager-history";
    const query = {
      params: {
        token: this.token,
      }
    };
    const conditionBean = this._modelTranslator.toReceptionDetailSearchConditionBean(conditions);
    this.outDataLog(conditionBean);
    const result = await this.post(url, conditionBean, query);
    this.outDataLog(result.data);

    return this._beanTranslator.toReceptionDetailSearchResult(result.data);
  }


  /**
   * 依頼詳細検索＆CSV出力
   * @param conditions 
   */
  async createCsvReceptionDetail(conditions: ReceptionDetailSearchCondition): Promise<ReceptionDetailCsvFilePrint> {
    const url = "/api/receptiondetails/ext/csv-create";
    const query = {
      params: {
        token: this.token,
      }
    };
    const conditionBean = this._modelTranslator.toReceptionDetailSearchConditionBean(conditions);
    this.outDataLog(conditionBean);
    const result = await this.post(url, conditionBean, query);
    this.outDataLog(result.data);

    return this._beanTranslator.toReceptionDetailCsvFilePrint(result.data, this.createDownloadCsvUrl(result.data));
  }

  /**
   * 依頼詳細（WF状態）検索＆CSV出力
   * @param conditions 
   */
  async createCsvReceptionDetailProgress(conditions: ReceptionDetailProgressSearchCondition): Promise<ReceptionDetailCsvFilePrint> {
    const url = "/api/receptiondetails/ext/csv-create-progress";
    const query = {
      params: {
        token: this.token,
      }
    };
    const conditionBean = this._modelTranslator.toReceptionDetailProgressSearchConditionBean(conditions);
    this.outDataLog(conditionBean);
    const result = await this.post(url, conditionBean, query);
    this.outDataLog(result.data);

    return this._beanTranslator.toReceptionDetailCsvFilePrint(result.data, this.createDownloadCsvUrl(result.data));
  }

  /**
   * 依頼詳細（プリセット）検索＆CSV出力
   * @param conditions 
   */
  async createCsvReceptionDetailPreset(conditions: ReceptionDetailSearchCondition): Promise<ReceptionDetailCsvFilePrint> {
    const url = "/api/receptiondetails/ext/csv-create-preset";
    const query = {
      params: {
        token: this.token,
      }
    };
    const conditionBean = this._modelTranslator.toReceptionDetailAllSearchConditionBean(conditions);
    this.outDataLog(conditionBean);
    const result = await this.post(url, conditionBean, query);
    this.outDataLog(result.data);
    
    return this._beanTranslator.toReceptionDetailCsvFilePrint(result.data, this.createDownloadCsvUrl(result.data));
  }

  createDownloadCsvUrl(bean: IReceptionDetailCsvFilePrintBean): string {
    return (!bean) ? null : "/api/receptiondetails/ext/csv-download?token=" + this.token + "&filePath=" + encodeURIComponent(bean.filePath);
  }

  /**
   * MyTODO検索
   * @param conditions 
   */
  async searchReceptionTodoMyself(conditions: ReceptionTodoSearchCondition): Promise<ReceptionDetailSearchResult> {
    const url = "/api/receptiontodo/search-myself";
    const query = {
      params: {
        token: this.token,
      }
    };
    const conditionBean = this._modelTranslator.toReceptionTodoSearchConditionBean(conditions);
    this.outDataLog(conditionBean);
    const result = await this.post(url, conditionBean, query);
    this.outDataLog(result.data);

    return this._beanTranslator.toReceptionDetailSearchResult(result.data);
  }

  /**
   * 所属診療科TODO検索
   * @param conditions 
   */
  async searchReceptionTodoDepartment(conditions: ReceptionTodoSearchCondition): Promise<ReceptionDetailSearchResult> {
    const url = "/api/receptiontodo/search-department";
    const query = {
      params: {
        token: this.token,
      }
    };
    const conditionBean = this._modelTranslator.toReceptionTodoSearchConditionBean(conditions);
    this.outDataLog(conditionBean);
    const result = await this.post(url, conditionBean, query);
    this.outDataLog(result.data);

    return this._beanTranslator.toReceptionDetailSearchResult(result.data);
  }

  /**
   * 差戻しTODO検索
   * @param conditions 
   */
  async searchReceptionTodoBacked(conditions: ReceptionTodoSearchCondition): Promise<ReceptionDetailSearchResult> {
    const url = "/api/receptiontodo/search-backed";
    const query = {
      params: {
        token: this.token,
      }
    };
    const conditionBean = this._modelTranslator.toReceptionTodoSearchConditionBean(conditions);
    this.outDataLog(conditionBean);
    const result = await this.post(url, conditionBean, query);
    this.outDataLog(result.data);

    return this._beanTranslator.toReceptionDetailSearchResult(result.data);
  }

  /**
   * 最終更新ワークフロー検索
   * @param conditions 
   */
  async searchReceptionTodoMyWritten(conditions: ReceptionTodoSearchCondition): Promise<ReceptionDetailSearchResult> {
    const url = "/api/receptiontodo/search-my-written";
    const query = {
      params: {
        token: this.token,
      }
    };
    const conditionBean = this._modelTranslator.toReceptionTodoSearchConditionBean(conditions);
    this.outDataLog(conditionBean);
    const result = await this.post(url, conditionBean, query);
    this.outDataLog(result.data);

    return this._beanTranslator.toReceptionDetailSearchResult(result.data);
  }

  /**
   * 依頼受付新規
   */
  async getNewReception(): Promise<ReceptionEditor> {
    const url = "/api/reception/create";
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return this._beanTranslator.toReceptionNewEditor(result.data);
  }

  /**
   * 依頼受付新規（受付コピー・引用）
   * @param receptionId 引用元受付ID
   */
  async getNewQuotedReception(receptionId: number, isActiveOnly: boolean, isQuote: boolean): Promise<ReceptionEditor> {
    const url = "/api/reception/" + receptionId + "/quote";
    const query = {
      params: {
        token: this.token,
        isActiveOnly: !!isActiveOnly,
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    // 画面から依頼文書の削除を許可するかの判断に使用する。引用のみ削除ができるので、引用時はfalseを渡す。
    return this._beanTranslator.toReceptionEditor(result.data, isQuote); 
  }

  /**
   * 依頼受付新規（依頼コピー・引用）
   * @param receptionDetailId 引用元依頼詳細ID
   */
  async getNewDetailQuotedReception(receptionDetailId: number, isQuote: boolean): Promise<ReceptionEditor> {
    const url = "/api/receptiondetail/" + receptionDetailId + "/quote";
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    // 画面から依頼文書の削除を許可するかの判断に使用する。依頼のコピーでは削除は不可とする。
    return this._beanTranslator.toReceptionEditor(result.data, isQuote);
  }

  /**
   * 依頼受付編集
   * @param receptionId 
   */
  async editReception(receptionId: number): Promise<ReceptionEditor> {
    const url = "/api/reception/" + receptionId + "/edit";
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return this._beanTranslator.toReceptionEditor(result.data, true);
  }

  /**
   * 依頼受付参照
   * @param receptionId 
   */
  async showReception(receptionId: number): Promise<ReceptionEditor> {
    const url = "/api/reception/" + receptionId;
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return this._beanTranslator.toReceptionEditor(result.data, false);
  }

  /**
   * 患者情報取得
   * @param patientId 
   */
  async getPatientInfo(patientId: string): Promise<ReceptionPatient> {
    const url = "/api/reception/patient/" + patientId;
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);

    return this._beanTranslator.toReceptionPatient(null, result.data);
  }

  /**
   * 依頼受付一時保存
   * @param reception 
   */
  async tempRegisterReception(reception: ReceptionEditor): Promise<ReceptionSaveResult> {
    // URL以外正式保存と一緒なんだけど・・
    const url = "/api/reception/temp-save";
    const query = { params: { token: this.token } };
    const receptionBean = this._modelTranslator.toReceptionEditorBean(reception);
    this.outDataLog(receptionBean);
    const result = await this.post(url, receptionBean, query);
    this.outDataLog(result.data);

    const saveResult = this._beanTranslator.toReceptionSaveResult(result.data);
    return saveResult;
  }

  /**
   * 依頼受付正式保存
   * @param reception 
   */
  async registerReception(reception: ReceptionEditor): Promise<ReceptionSaveResult> {
    const url = "/api/reception/save";
    const query = {
      params: {
        token: this.token
      }
    };
    const receptionBean = this._modelTranslator.toReceptionEditorBean(reception);
    this.outDataLog(receptionBean);
    const result = await this.post(url, receptionBean, query);
    this.outDataLog(result.data);

    const saveResult = this._beanTranslator.toReceptionSaveResult(result.data);
    return saveResult;
  }

  /**
   * 依頼受付削除
   * @param receptionId 
   */
  async deleteReception(receptionId: number): Promise<ReceptionSaveResult> {
    const url = "/api/reception/" + receptionId + "/delete";
    const query = {
      params: {
        token: this.token
      }
    };
    const result = await this.post(url, null, query);
    this.outDataLog(result.data);

    const saveResult = this._beanTranslator.toReceptionSaveResult(result.data);
    return saveResult;
  }

  /**
   * フォーマットに紐付くルートパターンを取得
   * @param formatOriginalId 
   */
  async getFormatRoutePattern(formatOriginalId: number): Promise<WorkflowRouteEditorModel> {
    const url = "/api/route/format/" + formatOriginalId;
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return this._beanTranslator.toWorkflowRouteEditor(result.data);
  }

  /**
   * パターン選択で選択可能なルートパターンを取得
   */
  async getSelectableRoutePatterns(): Promise<WorkflowRouteEditorModel[]> {
    const url = "/api/route/selectable";
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return this._beanTranslator.toWorkflowRouteEditors(result.data);
  }

  /**
   * 依頼詳細一覧取得
   * @param receptionId 受付ID
   */
  async getReceptionDetailList(receptionId: number): Promise<ReceptionDetailSummary[]> {
    const url = "/api/reception/" + receptionId + "/details";
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return this._beanTranslator.toReceptionDetailSummaryList(result.data);
  }

  /**
   * 依頼詳細（参照用）取得
   * @param receptionDetailId 依頼詳細ID
   */
  async getReceptionDetailReference(receptionDetailId: number): Promise<ReceptionReference> {
    const url = "/api/receptiondetail/" + receptionDetailId;
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return this._beanTranslator.toReceptionReference(result.data);
  }

  /**
   * 依頼詳細（参照用）を文書Idで取得
   * @param documentId 文書ID
   */
  async getReceptionReferenceByDocumentId(documentId: number): Promise<ReceptionReference> {
    const url = "/api/receptiondetail/document-reference/" + documentId;
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return this._beanTranslator.toReceptionReference(result.data);
  }

  /**
   * 依頼詳細（編集用）取得
   * @param receptionDetailId 依頼詳細ID
   */
  async getReceptionDetail(receptionDetailId: number): Promise<ReceptionEditor> {
    const url = "/api/receptiondetail/" + receptionDetailId + "/edit";
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return this._beanTranslator.toReceptionEditor(result.data, false);
  }

  /**
   * 依頼詳細削除
   * @param receptionDetailId 依頼詳細ID
   * @param deleteMessage 削除理由
   */
  async deleteReceptionDetail(receptionDetailId: number, deleteMessage: string): Promise<ReceptionSaveResult> {
    const url = "/api/receptiondetail/" + receptionDetailId + "/delete";
    const query = {
      params: {
        token: this.token,
      }
    };
    const delComment = {
      content: deleteMessage,
    };
    const result = await this.post(url, delComment, query);
    this.outDataLog(result.data);

    const saveResult = this._beanTranslator.toReceptionSaveResult(result.data);
    return saveResult;
  }

  /**
   * 依頼詳細保存
   * @param reception 
   */
  async registerReceptionDetail(reception: ReceptionEditor): Promise<ReceptionSaveResult> {
    const url = "/api/receptiondetail/save";
    const query = {
      params: {
        token: this.token
      }
    };
    const receptionBean = this._modelTranslator.toReceptionEditorBean(reception);
    this.outDataLog(receptionBean);
    const result = await this.post(url, receptionBean, query);
    this.outDataLog(result.data);

    const saveResult = this._beanTranslator.toReceptionSaveResult(result.data);
    return saveResult;
  }

  /**
   * 汎用フラグ1保存
   * @param receptionDetailId 依頼詳細ID
   * @param generalFlag 汎用フラグ1
   */
  async registerGeneralFlag1(receptionDetailId: number, generalFlag1: boolean): Promise<ReceptionSaveResult> {
    const url = "/api/receptiondetail/" + receptionDetailId + "/generalFlag1-save";
    const query = {
      params: {
        token: this.token,
      }
    };
    const delivery = { generalFlag1 };  // toReceptionDeliveryBean()を呼び出してもよかったが、不要な情報を渡さないように必要なものだけ

    this.outDataLog(delivery);
    const result = await this.post(url, delivery, query);
    this.outDataLog(result.data);

    const saveResult = this._beanTranslator.toReceptionSaveResult(result.data);
    return saveResult;
  }

  async registerManagers(receptionDetailId: number, managers: ReceptionManager[]): Promise<ReceptionSaveResult> {
    const url = "/api/receptiondetail/" + receptionDetailId + "/request-another";
    const query = {
      params: {
        token: this.token,
      }
    };
    const managersBean = this._modelTranslator.toReceptionManagerBeans(managers);
    const result = await this.post(url, managersBean, query);
    this.outDataLog(result.data);

    const saveResult = this._beanTranslator.toReceptionSaveResult(result.data);
    return saveResult;
  }

  /**
   * コメントセット取得
   * @param receptionDetailId 
   */
  async getCommentSet(receptionDetailId: number): Promise<ReceptionCommentSet> {
    const url = "/api/receptiondetail/" + receptionDetailId + "/commentset";
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return this._beanTranslator.toReceptionCommentSet(result.data);
  }

  /**
   * コメント取得
   * @param commentId 
   */
  async getComment(commentId: number): Promise<ReceptionComment> {
    const url = "/api/receptiondetail/" + commentId + "/comment";
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return this._beanTranslator.toReceptionComment(result.data);
  }

  async getCommentByDetailIdAndCommentType(receptionDetailId: number, commentType: string): Promise<ReceptionComment> {
    const url = "/api/receptiondetail/" + receptionDetailId + "/comment/" + commentType;
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return this._beanTranslator.toReceptionComment(result.data);
  }

  /**
   * コメント保存
   * @param receptionDetailId 依頼詳細ID
   * @param kind コメント種別
   * @param comment コメント
   */
  async registerComment(receptionDetailId: number, kind: ReceptionCommentKinds, comment: ReceptionComment): Promise<ReceptionSaveResult> {
    const url = "/api/receptiondetail/" + receptionDetailId + "/comment-save";
    const query = {
      params: {
        token: this.token,
        type: ReceptionCommentKindsName.getTypeName(kind),
      }
    };

    const commentBean = this._modelTranslator.toReceptionCommentBean(comment);
    this.outDataLog(commentBean);
    const result = await this.post(url, commentBean, query);
    this.outDataLog(result.data);

    const saveResult = this._beanTranslator.toReceptionSaveResult(result.data);
    return saveResult;
  }

  /**
   * コメント削除
   * @param commentId 
   */
  async deleteComment(commentId: number): Promise<ReceptionSaveResult> {
    const url = "/api/receptiondetail/comment/" + commentId + "/delete";
    const query = {
      params: {
        token: this.token
      }
    };
    const result = await this.post(url, null, query);
    this.outDataLog(result.data);

    const saveResult = this._beanTranslator.toReceptionSaveResult(result.data);
    return saveResult;
  }

  /**
   * 依頼書新規
   * @param receptionId 
   * @param patientId 
   */
  async getNewRequestSheetDocument(receptionId: number, formatOriginalId: number, patientId: string, isPublish: boolean): Promise<WorkflowDocument> {
    const workflowDocument = await this._getWorkflowDocument(
      async r => r.getNewDocument(patientId, formatOriginalId, null, null, null, null, null),
      "/api/reception/" + receptionId + "/request-sheet-create",
      {
        params: {
          token: this.token,
          patientCode: patientId,
          isPublish: !!isPublish,
        }
      });
    // 文書側のCreateTokenの他編集者の情報は破棄してWorkflowDocumentのcreateTokensのみで衝突管理する
    this.removeDocumentCreateTokenEditorInformations(workflowDocument);
    return workflowDocument;
  }

  private removeDocumentCreateTokenEditorInformations(workflowDocument: WorkflowDocument): void {
    if (!workflowDocument || !workflowDocument.document || !workflowDocument.document.createToken) { return; }
    // もしもワークフロー文書をオーダ番号も指定して登録したいという用途が出た場合は、そもそもどのようにゆるロック対応するかの方針検討から必要
    // また、登録時にバックエンドに返却する処理はあるが、使っていないし、将来的に使う見込みも無い
    workflowDocument.document.createToken.editors = [];
  }

  /**
   * 依頼書取得
   * @param receptionId 
   * @param patientId 
   */
  async getRequestSheetDocument(receptionId: number, documentId: number, patientId: string, editToken?: string, writeToken?: string): Promise<WorkflowDocument> {
    return this._getWorkflowDocument(
      async r => r.getDocument(patientId, documentId, editToken, writeToken, null)
      .then(d => {
        // 登録済み直接記載文書の一時保存を禁止する設定があり、バックエンドの編集文書取得APIで
        // その設定値をisRegisterableRegisteredDocumentAsTemporaryに格納している
        // 依頼書取得でもそのAPIを叩いているため設定の影響を受けてしまうので強制的にtrueに変更している
        d.attributes.isRegisterableRegisteredDocumentAsTemporary = true;
        // 上記と同様に、直接記載の一時保存文書で印刷を禁止する設定があり
        // 依頼書取得でもその影響を受けてしまうので強制的にtrueに変更している
        d.attributes.isPrintableRegisteredDocumentAsTemporary = true;
        return d;
      }),
      "/api/reception/" + receptionId + "/request-sheet-edit",
      {
        params: {
          token: this.token,
          patientCode: patientId,
        }
      });
  }

  /**
   * 依頼書取得（参照用：主にPermission）
   * @param receptionId 
   * @param patientId 
   */
  async getRequestSheetDocumentViewer(receptionId: number, patientId: string): Promise<WorkflowDocumentViewer> {
    const url = "/api/reception/" + receptionId + "/request-sheet-show";
    const query = {
      params: {
        token: this.token,
        patientCode: patientId
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return this._beanTranslator.toWorkflowDocumentViewer(result.data);
  }

  /**
   * 依頼書保存
   * @param comment 
   */
  async registerRequestSheetDocument(receptionId: number, document: WorkflowDocument): Promise<WorkflowDocumentSaveResult> {
    const url = "/api/reception/" + receptionId + "/request-sheet-save";
    const query = {
      params: {
        token: this.token
      }
    };
    const documentBean = this._modelTranslator.toWorkflowDocumentBean(document);
    this.outDataLog(documentBean);
    const result = await this.post(url, documentBean, query);
    this.outDataLog(result.data);

    const saveResult = this._beanTranslator.toWorkflowDocumentSaveResult(result.data);
    return saveResult;
  }

  /**
   * 依頼書削除
   */
  async deleteRequestSheetDocument(receptionId: number, patientId: string, documentId: number): Promise<WorkflowDocumentSaveResult> {
    const url = "/api/reception/" + receptionId + "/request-sheet-delete";
    const query = {
      params: {
        token: this.token,
        patientCode: patientId,
        documentId,
      }
    };
    const result = await this.post(url, null, query);
    const saveResult = this._beanTranslator.toWorkflowDocumentSaveResult(result.data);
    return saveResult;
  }

  /**
   * フォーマット管理タイプ取得
   * @param formatOriginalId 
   */
  async getFormatManageType(formatOriginalId: number): Promise<DocumentManageKinds> {
    if (formatOriginalId === null || formatOriginalId === undefined || isNaN(formatOriginalId)) {
      throw new Error("formatOriginalId is invalid!!!");
    }
    await this.waitForPrefetchResult();
    const prefetchedData = (window as any).prefetchedData;
    if (prefetchedData) {
      const prefetched = prefetchedData.formatManageType;
      if (prefetched && prefetched.formatOriginalId === formatOriginalId && prefetched.manageType) {
        this._logger.info("プリフェッチされたmanageTypeを使用します。formatOriginalId=" + prefetched.formatOriginalId + ", manageType=" + prefetched.manageType);
        return prefetched.manageType;
      }
    }
    const url = "/api/dispatch/format/" + formatOriginalId + "/managetype";
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return result.data as number;
  }

  async waitForPrefetchResult(): Promise<void> {
    const prefetchedData = (window as any).prefetchedData;
    if (!prefetchedData) { return; }
    if (prefetchedData.formatManageType) { return; }
    await AsyncUtility.sleep(10);
  }

  /**
   * 文書管理タイプ取得
   * @param documentId 
   * @param patientId 
   */
  async dispatchDocument(documentId: number, patientId: string, ignoreFixKey: string): Promise<DispatchDocumentResult> {
    const url = "/api/dispatch/document/" + documentId + "/managetype";
    const query = {
      params: {
        token: this.token,
        patientCode: patientId,
        ignoreFixKey: !!ignoreFixKey ? ignoreFixKey : "",
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return this._beanTranslator.toDispatchDocumentResult(result.data);
  }

  /**
   * WF文書新規（自動登録用）
   * @param formatOriginalId 
   * @param patientId 
   */
  async getNewWorkflowAutoDocument(formatOriginalId: number, patientId: string, institutionNo: string, regionalInstitutionNo: string, hisInfoGetterParameter: string, systemKey: string, linkKey: string): Promise<WorkflowDocument> {
    // 自動登録の場合は文書側のCreateTokenの他編集者の情報は破棄しない
    return this._getWorkflowDocument(
      async r => r.getNewDocument(patientId, formatOriginalId, institutionNo, regionalInstitutionNo, hisInfoGetterParameter, systemKey, linkKey),
      "/api/receptiondetail/autoregister/document-create",
      {
        params: {
          token: this.token,
          patientCode: patientId,
          formatOriginalId
        }
      });
  }

  /**
   * WF文書新規（自動登録連鎖用）
   * @param sourceFormatOriginalId 
   * @param formatOriginalId 
   * @param patientId 
   * @param institutionNo 
   * @param regionalInstitutionNo 
   * @param hisInfoGetterParameter 
   * @param linkKey 
   */
  async getNewWorkflowAutoChainDocument(sourceFormatOriginalId: number, formatOriginalId: number, patientId: string, institutionNo: string, regionalInstitutionNo: string, hisInfoGetterParameter: string, systemKey: string, linkKey: string): Promise<WorkflowDocument> {
    // 自動登録の場合は文書側のCreateTokenの他編集者の情報は破棄しない
    return this._getWorkflowDocument(
      async r => r.getNewDocument(patientId, formatOriginalId, institutionNo, regionalInstitutionNo, hisInfoGetterParameter, systemKey, linkKey),
      "/api/receptiondetail/autoregister/document-create",
      {
        params: {
          token: this.token,
          patientCode: patientId,
          formatOriginalId: sourceFormatOriginalId
        }
      });
  }

  /**
   * WF文書コピー作成（自動登録用）
   */
  async getNewCopiedWorkflowAutoDocument(copiedDocumentId: number, patientId: string): Promise<WorkflowDocument> {
    // 自動登録の場合は文書側のCreateTokenの他編集者の情報は破棄しない
    return this._getWorkflowDocument(
      async r => r.getNewCopiedDocument(patientId, copiedDocumentId),
      "/api/receptiondetail/autoregister/document-copy",
      {
        params: {
          token: this.token,
          patientCode: patientId,
          copiedDocumentId
        }
      });
  }

  /**
   * WF文書新規
   * @param receptionDetailId 
   * @param patientId 
   */
  async getNewWorkflowDocument(receptionDetailId: number, formatOriginalId: number, patientId: string, isDocumentAutoRegister: boolean): Promise<WorkflowDocument> {
    const workflowDocument = await this._getWorkflowDocument(
      async r => r.getNewDocument(patientId, formatOriginalId, null, null, null, null, null),
      "/api/receptiondetail/" + receptionDetailId + "/document-create",
      {
        params: {
          token: this.token,
          patientCode: patientId,
          isDocumentAutoRegister: !!isDocumentAutoRegister,
        }
      });
    // 文書側のCreateTokenの他編集者の情報は破棄してWorkflowDocumentのcreateTokensのみで衝突管理する
    this.removeDocumentCreateTokenEditorInformations(workflowDocument);
    return workflowDocument;
  }

  async getForceChainWorkflowDocument(receptionDetailId: number, formatOriginalId: number, patientId: string): Promise<WorkflowDocument> {
    return this._getWorkflowDocument(
      async r => r.getNewDocument(patientId, formatOriginalId, null, null, null, null, null),
      "/api/receptiondetail/" + receptionDetailId + "/document-edit",
      {
        params: {
          token: this.token,
          patientCode: patientId
        }
      });
  }

  /**
   * WF文書コピー作成
   */
  async getNewCopiedWorkflowDocument(receptionDetailId: number, copiedDocumentId: number, patientId: string): Promise<WorkflowDocument> {
    const workflowDocument = await this._getWorkflowDocument(
      async r => r.getNewCopiedDocument(patientId, copiedDocumentId),
      "/api/receptiondetail/" + receptionDetailId + "/document-create",
      {
        params: {
          token: this.token,
          patientCode: patientId,
          isDocumentAutoRegister: false,
        }
      });
    // 文書側のCreateTokenの他編集者の情報は破棄してWorkflowDocumentのcreateTokensのみで衝突管理する
    this.removeDocumentCreateTokenEditorInformations(workflowDocument);

    const info = await this.getReceptionDetailReference(receptionDetailId);
    if (info.receptionDetail.format.id !== workflowDocument.document.attributes.format.originalId) {
      throw new Error("依頼に指定されたフォーマットとコピー対象の文書のフォーマットが一致していません。");
    }

    return workflowDocument;
  }

  /**
   * WF文書取得
   * @param receptionDetailId 
   * @param patientId 
   */
  async getWorkflowDocument(receptionDetailId: number, documentId: number, patientId: string, editToken?: string, writeToken?: string): Promise<WorkflowDocument> {
    return this._getWorkflowDocument(
      async r => r.getDocument(patientId, documentId, editToken, writeToken, null)
      .then(d => {
        // 登録済み直接記載文書の一時保存を禁止する設定があり、バックエンドの編集文書取得APIで
        // その設定値をisRegisterableRegisteredDocumentAsTemporaryに格納している
        // WF文書取得でもそのAPIを叩いているため設定の影響を受けてしまうので強制的にtrueに変更している
        d.attributes.isRegisterableRegisteredDocumentAsTemporary = true;
        // 上記と同様に、直接記載の一時保存文書で印刷を禁止する設定があり
        // WF文書取得でもその影響を受けてしまうので強制的にtrueに変更している
        d.attributes.isPrintableRegisteredDocumentAsTemporary = true;
        return d;
      }),
      "/api/receptiondetail/" + receptionDetailId + "/document-edit",
      {
        params: {
          token: this.token,
          patientCode: patientId
        }
      });
  }

  private async _getWorkflowDocument(create: (r: IDocumentRepository) => Promise<Document>, url: string, config?: AxiosRequestConfig): Promise<WorkflowDocument> {
    const results = await Promise.all([
      this.get<IWorkflowDocumentSupplementBean>(url, config),
      this.fetch(
        DomainRegistry.document.getDocumentRepository(),
        r => create(r)),
    ]);
    this.outDataLog(results[0].data);
    return this._beanTranslator.createWorkflowDocument(results[1], results[0].data);
  }

  /**
   * WF文書情報取得
   * @param receptionDetailId 
   * @param patientId 
   */
  async getWorkflowDocumentInfo(receptionDetailId: number, patientId: string): Promise<WorkflowDocumentInfo> {
    const url = "/api/receptiondetail/" + receptionDetailId + "/document-info";
    const query = {
      params: {
        token: this.token,
        patientCode: patientId
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return this._beanTranslator.toWorkflowDocumentInfo(result.data);
  }

  /**
   * WF文書取得（参照用：主にPermission）
   * @param receptionDetailId 
   * @param patientId 
   */
  async getWorkflowDocumentViewer(receptionDetailId: number, patientId: string): Promise<WorkflowDocumentViewer> {
    const url = "/api/receptiondetail/" + receptionDetailId + "/document-show";
    const query = {
      params: {
        token: this.token,
        patientCode: patientId
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return this._beanTranslator.toWorkflowDocumentViewer(result.data);
  }

  /**
   * WF新規文書の表示情報取得
   * @param receptionDetailId 
   * @param patientId 
   */
  async getNewWorkflowDocumentViewer(receptionDetailId: number, patientId: string): Promise<WorkflowDocumentViewer> {
    const url = "/api/receptiondetail/" + receptionDetailId + "/new-document-view-model-create";
    const query = {
      params: {
        token: this.token,
        patientCode: patientId
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return this._beanTranslator.toWorkflowDocumentViewer(result.data);
  }

  /**
   * WF文書保存
   * @param comment 
   */
  async registerWorkflowDocument(receptionDetailId: number, document: WorkflowDocument): Promise<WorkflowDocumentSaveResult> {
    const url = "/api/receptiondetail/" + receptionDetailId + "/document-save";
    const query = {
      params: {
        token: this.token
      }
    };
    const documentBean = this._modelTranslator.toWorkflowDocumentBean(document);
    this.outDataLog(documentBean);
    const result = await this.post(url, documentBean, query);
    this.outDataLog(result.data);

    const saveResult = this._beanTranslator.toWorkflowDocumentSaveResult(result.data);
    return saveResult;
  }

  /**
   * WF文書保存（競合あり）
   * @param comment 
   */
  async forceRegisterWorkflowDocument(receptionDetailId: number, document: WorkflowDocument, conflictType: WorkflowDocumentConflictKinds): Promise<WorkflowDocumentSaveResult> {
    const url = "/api/receptiondetail/" + receptionDetailId + "/document-save-force";
    const query = {
      params: {
        token: this.token,
        conflictType,
      }
    };
    const documentBean = this._modelTranslator.toWorkflowDocumentBean(document);
    this.outDataLog(documentBean);
    const result = await this.post(url, documentBean, query);
    this.outDataLog(result.data);

    const saveResult = this._beanTranslator.toWorkflowDocumentSaveResult(result.data);
    return saveResult;
  }

  /**
   * 文書を保存（WF自動登録）
   */
  async registerWorkflowAutoDocument(document: WorkflowDocument): Promise<WorkflowDocumentSaveResult> {
    const url = "/api/receptiondetail/autoregister/document-save";
    const query = {
      params: {
        token: this.token
      }
    };
    const documentBean = this._modelTranslator.toWorkflowDocumentBean(document);
    this.outDataLog(documentBean);
    const result = await this.post(url, documentBean, query);
    this.outDataLog(result.data);

    const saveResult = this._beanTranslator.toWorkflowDocumentSaveResult(result.data);
    return saveResult;
  }

  /**
   * WFアクション実行
   * @param receptionDetailId 
   * @param action 
   */
  async doWorkflowAction(receptionDetailId: number, action: WorkflowActionContent): Promise<ReceptionSaveResult> {
    const url = "/api/receptiondetail/" + receptionDetailId + "/action";
    const query = {
      params: {
        token: this.token
      }
    };
    const actionBean = this._modelTranslator.toWorkflowActionContentBean(action);
    this.outDataLog(actionBean);
    const result = await this.post(url, actionBean, query);
    this.outDataLog(result.data);

    const saveResult = this._beanTranslator.toReceptionSaveResult(result.data);
    return saveResult;
  }

  /**
   * プリセット情報を登録
   * @param conditions
   */
  async registerPresetCondition(condition: ReceptionDetailPresetSearchCondition): Promise<string> {
    const url = "/api/receptiondetail/preset/save";
    const query = {
      params: {
        token: this.token
      }
    };
    const conditionBean = this._modelTranslator.toPresetConditionBean(condition);
    this.outDataLog(conditionBean);
    const result = await this.post(url, conditionBean, query);
    this.outDataLog(result.data);
    return result.data.key;
  }

  /**
   * プリセット情報を取得
   * @param departmentId 
   */
  async getPresetCondition(departmentId: string): Promise<ReceptionDetailPresetSearchCondition[]> {
    const url = "/api/receptiondetail/preset/" + departmentId;
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return this._beanTranslator.toReceptionDetailPresetSearchConditions(result.data);
  }

  /**
   * プリセット情報を削除
   * @param condition 
   */
  async deletePresetCondition(condition: ReceptionDetailPresetSearchCondition): Promise<void> {
    const url = "/api/receptiondetail/preset/delete";
    const query = {
      params: {
        token: this.token
      }
    };
    const conditionBean = this._modelTranslator.toPresetConditionBean(condition);
    this.outDataLog(conditionBean);
    const result = await this.post(url, conditionBean, query);
    this.outDataLog(result.data);
  }

  /**
   * プリセットの並び順情報を登録
   * @param order
   */
  async registerPresetOrder(keys: string[]): Promise<void> {
    const url = "/api/receptiondetail/preset/order/save";
    const query = {
      params: {
        token: this.token
      }
    };
    const orderBean = this._modelTranslator.toPresetSearchConditionOrderBeans(keys);
    this.outDataLog(orderBean);
    const result = await this.post(url, orderBean, query);
    this.outDataLog(result.data);
  }

  /**
   * プリセットの並び順情報を取得
   */
  async getPresetOrder(): Promise<string[]> {
    const url = "/api/receptiondetail/preset/order";
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return this._beanTranslator.toReceptionDetailPresetSearchConditionKeys(result.data);
  }

  async getCurrentCreatorInformation(key: number, exclusionToken: string, isWorkflowDocument: boolean): Promise<CurrentEditors[]> {
    const url = "/api/receptiondetail/" + key + "/current-creator-information";
    const query = {
      params: {
        token: this.token,
        exclusionToken: exclusionToken,
        isWorkflowDocument: isWorkflowDocument,
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return this._beanTranslator.toCurrentEditors(result.data);
  }

  async deleteDocumentCreateToken(createToken: string): Promise<void> {
    if (!createToken) { return; }
    
    const url = "/api/receptiondetail/" + createToken + "/document-create-token-delete";
    const query = {
      params: {
        token: this.token,
      }
    };
    await this.post(url, query);
  }

  async extendDocumentCreateTokenLifeSpan(createToken: string): Promise<void> {
    if (!createToken) { return; }
    
    const url = "/api/receptiondetail/" + createToken + "/document-create-token-extend";
    const query = {
      params: {
        token: this.token,
      }
    };
    await this.post(url, query);
  }

  /**
   * ソート条件プリセット情報を取得
   */
  async getPresetSortCondition(): Promise<ReceptionDetailPresetSortCondition[]> {
    const url = "/api/receptiondetail/sort/preset";
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    return this._beanTranslator.toReceptionDetailPresetSortConditions(result.data);
  }

  /**
   * ソート条件プリセット情報を登録
   * @param conditions
   */
  async registerPresetSortCondition(conditions: ReceptionDetailPresetSortCondition[]): Promise<void> {
    const url = "/api/receptiondetail/sort/preset/save";
    const query = {
      params: {
        token: this.token
      }
    };
    const conditionBeans = this._modelTranslator.toPresetSortConditionBeans(conditions);
    await this.post(url, conditionBeans, query);
  }

  /**
   * 初回検索条件用診療科コードを取得
   */
  async getSearchConditionDepartmentCodes(): Promise<string[]> {
    const url = "/api/reception/search-condition/department-codes";
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    return result.data;
  }

  /**
   * 初回検索条件用診療科コードを登録
   * @param departmentCodes
   */
  async registerSearchConditionDepartmentCodes(departmentCodes: string[]): Promise<void> {
    const url = "/api/reception/search-condition/department-codes/save";
    const query = {
      params: {
        token: this.token
      }
    };
    await this.post(url, departmentCodes, query);
  }

  /**
   * 依頼受付にひもづく依頼詳細の管理外文書情報を取得
   * @param receptionId 依頼書ID
   */
  async getUnmanagedDocumentSummaryInformations(receptionId: number): Promise<UnmanagedDocumentSummaryInformation[]> {
    const url = "/api/reception/quote-documents/" + receptionId;
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return this._beanTranslator.toUnmanagedDocumentSummaryInformations(result.data);
  }

  /**
   * 文書に関連したシステムログを追加
   * @param patientCode
   * @param receptionId
   * @param receptionDetailId
   * @param message
   */
  async addReceptionDocumentOperationSystemLog(patientCode: string, receptionId: number, receptionDetailId: number, message: string): Promise<void> {
    const url = "api/receptiondetail/document/add-operation-system-log";
    const data: IWorkflowDocumentOperationMessageBean = {
      token: this.token,
      patientCode: patientCode,
      message: message,
      receptionId: receptionId,
      receptionDetailId: receptionDetailId,
    };
    await this.post(url, data);
  }

  /**
   * WF一覧リスト項目取得
   */
  async getDetailListItem(): Promise<ReceptionDetailListItem[]> {
    const url = "/api/receptiondetail/sort/list-item";
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    return this._beanTranslator.toDisplayItemList(result.data);
  }

  private outDataLog(data: any) {
    console.debug("[data]", data);
  }

  /**
   * 依頼書用のHIS情報取得
   */
  async getRequestSheeHisInformation(patientCode: string, receptionId: number): Promise<WorkflowHisInformation> {
    const url = "/api/reception/" + receptionId + "/request-sheet-his-information";
    const query = {
      params: {
        token: this.token,
        patientCode: patientCode,
      }
    };
    const result = await this.get(url, query);
    return this._beanTranslator.toWorkflowHisInformation(result.data);
  }

  /**
   * WF文書用のHIS情報取得
   */
  async getWorkflowDocumentHisInformation(patientCode: string, receptionDetailId: number): Promise<WorkflowHisInformation> {
    const url = "/api/receptiondetail/" + receptionDetailId + "/document-his-information";
    const query = {
      params: {
        token: this.token,
        patientCode: patientCode,
      }
    };
    const result = await this.get(url, query);
    return this._beanTranslator.toWorkflowHisInformation(result.data);
  }

  /**
   * 外部連携した依頼詳細IDから依頼情報ID取得
   */
  async getReceptionDetailIdFromExternalReceptionDetailId(externalReceptionDetailId: number, patientNo: string): Promise<number> {
    const url = "/api/receptiondetail/external-reference/" + externalReceptionDetailId + "/id";
    const query = {
      params: {
        token: this.token,
        patientCode: patientNo,
      }
    };
    const result = await this.get(url, query);
    this.outDataLog(result.data);
    return result.data;
  }

  /**
   * 依頼受付に紐づくトークンを取得
   */
  async getReceptionIdDocumentTokens(receptionId: number): Promise<CurrentEditors[]> {
    const url = "/api/reception/" + receptionId + "/document-tokens";
    const result = await this.get(url);
    return this._beanTranslator.toCurrentEditors(result.data);
  }

  /**
   * 依頼詳細に紐づくWF文書のトークンを取得
   */
  async getReceptionDetailIdDocumentTokens(receptionDetailId: number): Promise<CurrentEditors[]> {
    const url = "/api/receptiondetail/" + receptionDetailId + "/document-tokens";
    const result = await this.get(url);
    return this._beanTranslator.toCurrentEditors(result.data);
  }
}
